import {
  CheckCircle,
  Circle,
  CircleDashed,
  Lightbulb,
} from "@tamagui/lucide-icons";
import { makeAutoObservable, observable, reaction, runInAction } from "mobx";
import {
  deleteInLS,
  getStringInLS,
  saveStringInLS,
} from "../core/localStorage";
import { trackEvent } from "../core/trackEvent";
import type { IconProp } from "../ui/icons";

export enum HelperType {
  Poll = 1,
  Plan = 2,
  DateDue = 3,
  Checkbox = 4,
  PlanInfo = 5,
  Calendar = 6,
  Location = 7,
  Expense = 8,
  Reminder = 9,
}

export enum HelpItemRequiredType {
  Required = 0,
  Optional = 1,
  Tip = 2,
}

export type HelpItem = {
  type: HelpItemRequiredType;
  label: string;
  isCompleted: () => boolean;
  isEnabled?: () => boolean;
};

const resetHelperStore = observable(new Set<string>());

export const doResetHelperStore = (refUid: string) =>
  runInAction(() => {
    resetHelperStore.add(refUid);
  });

export class HelperManager {
  public currentStepIndex = 0;
  public isVisible = false;

  private items: HelpItem[] = [];

  constructor(
    private type: HelperType,
    private refUid: string,
  ) {
    makeAutoObservable(this);

    // Check if closed before
    if (__DEV__ || !getStringInLS(this.lsKey)) {
      this.isVisible = true;
    }

    reaction(
      () => !!resetHelperStore.has(refUid),
      (doReset) => {
        if (doReset) {
          this.showAgain();
        }
      },
    );
  }

  updateItems(items: HelpItem[]) {
    const itemsEnabled = items.filter(
      (i) => i.isEnabled === undefined || i.isEnabled(),
    );

    if (
      this.isVisible &&
      this.items.length === 0 &&
      items.length > 0 &&
      itemsEnabled.every((i) => i.isCompleted())
    ) {
      // First update, are all completed? If so don't set it as visible this time

      this.isVisible = false;
    }

    this.items = itemsEnabled;
    this.gotoFirstUncompletedStep();
  }

  async gotoFirstUncompletedStep() {
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      if (!item.isCompleted()) {
        this.currentStepIndex = i;
        return;
      }
    }

    this.currentStepIndex = this.items.length - 1;
  }

  trackEventAction(action: string) {
    trackEvent(`helper_${this.type}_${action}`);
  }

  gotoPrevStep() {
    if (this.currentStepIndex - 1 === -1) {
      this.currentStepIndex = this.items.length - 1;
    } else {
      this.currentStepIndex = this.currentStepIndex - 1;
    }

    this.trackEventAction("prev");
  }

  gotoNextStep() {
    this.currentStepIndex = (this.currentStepIndex + 1) % this.items.length;

    this.trackEventAction("next");
  }

  gotoStep(index: number) {
    this.currentStepIndex = index;

    this.trackEventAction("goto");
  }

  dismiss() {
    saveStringInLS(this.lsKey, "seen");
    this.isVisible = false;

    this.trackEventAction("dismiss");
  }

  showAgain() {
    deleteInLS(this.lsKey);
    this.isVisible = true;
    resetHelperStore.delete(this.refUid);

    this.trackEventAction("show_again");
  }

  get lsKey() {
    return `helper_${this.refUid}_${this.type}`;
  }

  get currentItem() {
    return this.items[this.currentStepIndex];
  }

  get icons(): { icon: IconProp; isCurrent: boolean }[] {
    return this.items.map((item, i) => {
      const isCurrent = i === this.currentStepIndex;

      if (item.type === HelpItemRequiredType.Required) {
        return {
          icon: item.isCompleted() ? CheckCircle : Circle,
          isCurrent,
        };
      }

      if (item.type === HelpItemRequiredType.Optional) {
        return {
          icon: item.isCompleted() ? CheckCircle : CircleDashed,
          isCurrent,
        };
      }

      return {
        icon: Lightbulb,
        isCurrent,
      };
    });
  }

  get isFirstStep() {
    return this.currentStepIndex === 0;
  }

  get isLastStep() {
    return this.items.length - 1 === this.currentStepIndex;
  }

  get isComplete(): boolean {
    return this.items.length
      ? this.items
          .filter((item) => item.isEnabled?.() ?? true)
          .every((i) => i.isCompleted())
      : false;
  }
}
